import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from 'react-auth-kit';
import { NAVIGATION_ROUTES } from './utils/NavigationUtils';
import RequireAuth from './RequireAuth';

// component imports
import MobileSplashScreen from './components/MobileSplashScreen/MobileSplashScreen';
import LogIn from './components/LogIn/LogIn';
import AllUsers from './components/Users/AllUsers/AllUsers';
import UserManager from './components/Users/UserManager/UserManager';
import KPIs from './components/KPIs/KPIs';
import AdminManagement from './components/AdminManagement/AdminManagement';
import Affiliates from './components/Affiliates/Affiliates';
import Events from './components/Events/Events';
import Dashboard from './components/Dashboard/Dashboard';
import Promotions from './components/Promotions/Promotions';
import WhiteList from './components/WhiteList/WhiteList';
import UsersNew from './components/Users/UsersNew/UsersNew';

import './App.css';
import './fonts.css';
import './table.css';
import '@fontsource/sora/600.css'; // 600 is the weight for Semibold
import Payments from './components/Payments/Payments';
import Players from './components/Players/Players';
import LocationRestrictions from './components/LocationRestrictions/LocationRestrictions';
import Reports from './components/Reports/Reports';
import MarketingAffiliates from './components/Marketing/Affiliates/MarketingAffiliates';
import Sportsbook from './components/Sportsbook/Sportsbook';
import Casino from './components/Casino/Casino';
import ManualEntries from './components/ManualEntries/ManualEntries';
import PromotionsManagement from './components/Promotions/PromotionsManagement/PromotionsManagement';
import CampaignDetails from './components/Promotions/PromotionsManagement/CampaignDetails/CampaignDetails';
import Transactions from './components/Transactions/Transactions';
import NewPromotion from './components/Promotions/NewPromotion/NewPromotion';
import Wallet from './components/Transactions/Wallet/Wallet';
import TransactionsOverview from './components/Transactions/TransactionsOverview/TransactionsOverview';
import PrizeRedemptionManualReview from './components/Payments/PrizeRedemptionManualReview/PrizeRedemptionManualReview';
import PrizeRedemptionManualReviewTracking from './components/Payments/PrizeRedemptionManualReviewTracking/PrizeRedemptionManualReviewTracking';
import CustomerSupportControls from './components/AdminManagement/CustomerSupportControls/CustomerSupportControls';
import TransactionsTable from './components/Transactions/TransactionsTable/TransactionsTable';
import BetsReport from './components/Sportsbook/BetsReport/BetsReport';
import EventDetails from './components/Sportsbook/EventDetails/EventDetails';
import BetsTypesList from './components/Sportsbook/EventDetails/EventKpi/BetsTypesList/BetsTypesList';
import WithdrawalReview from './components/Transactions/WithdrawalReview/WithdrawalReview';
import WithdrawalReviewRules from './components/Transactions/WithdrawalReview/WithdrawalReviewRules/WithdrawalReviewRules';
import AddRulePage from './components/Transactions/WithdrawalReview/WithdrawalReviewRules/AddRulePage/AddRulePage';
import EditRulePage from './components/Transactions/WithdrawalReview/WithdrawalReviewRules/EditRulePage/EditRulePage';
import MainSportsbook from "./components/Sportsbook/MainSportsbook/MainSportsbook";
import SportsbookFM from "./components/Configurations/SportsbookFM/SportsbookFM";
import CasinoFM from "./components/Configurations/CasinoFM/CasinoFm";

const App = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 650); // Example width for mobile devices
    };

    handleResize(); // Check the initial screen size
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div className={isMobile ? 'mobile-app-container' : 'app-container'}>
      {isMobile ? (
        <MobileSplashScreen />
      ) : (
        <AuthProvider
          authType={'cookie'}
          authName={'_auth'}
          cookieDomain={window.location.hostname}
          cookieSecure={false}
        >
          <BrowserRouter>
            {/* Each route is given separation to allow for visual consistency and protected routes */}
            <Routes>
              {/* Unprotected routes */}
              <Route path={NAVIGATION_ROUTES?.LOGIN} element={<LogIn />} />

              {/* Protected routes */}
              <Route
                path={NAVIGATION_ROUTES?.DASHBOARD}
                element={
                  <RequireAuth>
                    <Dashboard />
                  </RequireAuth>
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.USERS}
                element={
                  <RequireAuth>
                    <AllUsers />
                  </RequireAuth>
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.USERS_NEW}
                element={
                  <RequireAuth>
                    <UsersNew />
                  </RequireAuth>
                }
              />
              {/* Protected routes */}
              <Route
                path={NAVIGATION_ROUTES?.DASHBOARD}
                element={
                  <RequireAuth>
                    <Dashboard />
                  </RequireAuth>
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.PLAYERS}
                element={
                  <RequireAuth>
                    <Players />
                  </RequireAuth>
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.USER_MANAGER}
                element={
                  <RequireAuth>
                    <UserManager />
                  </RequireAuth>
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.CASINO}
                element={
                  <RequireAuth>
                    <Casino />
                  </RequireAuth>
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.SPORTSBOOK}
                element={
                  <RequireAuth>
                    <Sportsbook />
                  </RequireAuth>
                }
              />
              <Route
                  path={NAVIGATION_ROUTES?.MAIN_SPORTSBOOK}
                  element={
                    <RequireAuth>
                      <MainSportsbook />
                    </RequireAuth>
                  }
              />
              <Route
                path={NAVIGATION_ROUTES?.SPORTSBOOK_BETS_REPORT}
                element={
                  <RequireAuth>
                    <BetsReport />
                  </RequireAuth>
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.KPIS}
                element={
                  <RequireAuth>
                    <KPIs />
                  </RequireAuth>
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.AFFILIATES}
                element={
                  <RequireAuth>
                    <Affiliates />
                  </RequireAuth>
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.EVENTS}
                element={
                  <RequireAuth>
                    <Events />
                  </RequireAuth>
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.CREATE_ACCOUNT}
                element={
                  <RequireAuth>
                    <AdminManagement />
                  </RequireAuth>
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.CUSTOMER_SUPPORT_CONTROLS}
                element={
                  <RequireAuth>
                    <CustomerSupportControls />
                  </RequireAuth>
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.AGENT_DASHBOARD}
                element={
                  <RequireAuth>
                    <WhiteList />
                  </RequireAuth>
                }
              />

              <Route
                path={NAVIGATION_ROUTES?.MANUAL_ENTRIES}
                element={
                  <RequireAuth>
                    <ManualEntries />
                  </RequireAuth>
                }
              />

              <Route
                path={NAVIGATION_ROUTES?.FINIX_DELAYED_PAYMENT}
                element={
                  <RequireAuth>
                    <Payments />
                  </RequireAuth>
                }
              />

              <Route
                path={NAVIGATION_ROUTES?.PRIZE_REDEMPTION_MANUAL_REVIEW}
                element={
                  <RequireAuth>
                    <PrizeRedemptionManualReview />
                  </RequireAuth>
                }
              />
              <Route
                path={
                  NAVIGATION_ROUTES?.PRIZE_REDEMPTION_MANUAL_REVIEW_TRACKING
                }
                element={
                  <RequireAuth>
                    <PrizeRedemptionManualReviewTracking />
                  </RequireAuth>
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.LOCATIONS_RESTRICTIONS}
                element={
                  <RequireAuth>
                    <LocationRestrictions />
                  </RequireAuth>
                }
              />
              <Route
                  path={NAVIGATION_ROUTES?.CASINO_FM}
                  element={
                    <RequireAuth>
                      <CasinoFM />
                    </RequireAuth>
                  }
              />
              <Route
                  path={NAVIGATION_ROUTES?.SPORTSBOOK_FM}
                  element={
                    <RequireAuth>
                      <SportsbookFM />
                    </RequireAuth>
                  }
              />
              <Route
                path={NAVIGATION_ROUTES?.MARKETING_AFFILIATES}
                element={
                  <RequireAuth>
                    <MarketingAffiliates />
                  </RequireAuth>
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.REPORTS}
                element={
                  <RequireAuth>
                    <Reports />
                  </RequireAuth>
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.PROMOTIONS}
                element={
                  <RequireAuth>
                    <Promotions />
                  </RequireAuth>
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.PROMOTIONS_MANAGEMENT}
                element={
                  <RequireAuth>
                    <PromotionsManagement />
                  </RequireAuth>
                }
              />
              <Route
                path="/promotions-management/campaign/:slug"
                element={
                  <RequireAuth>
                    <CampaignDetails />
                  </RequireAuth>
                }
              />
              <Route
                path="/promotions-management/new-promotion"
                element={
                  <RequireAuth>
                    <NewPromotion />
                  </RequireAuth>
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.TRANSACTIONS_PROCESSORS}
                element={
                  <RequireAuth>
                    <Transactions />
                  </RequireAuth>
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.TRANSACTIONS_WALLET}
                element={
                  <RequireAuth>
                    <Wallet />
                  </RequireAuth>
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.TRANSACTIONS_OVERVIEW}
                element={
                  <RequireAuth>
                    <TransactionsOverview />
                  </RequireAuth>
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.TRANSACTIONS_REPORTS}
                element={
                  <RequireAuth>
                    <TransactionsTable />
                  </RequireAuth>
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.WITHDRAWAL_REVIEW}
                element={
                  <RequireAuth>
                    <WithdrawalReview />
                  </RequireAuth>
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.WITHDRAWAL_REVIEW_RULES}
                element={
                  <RequireAuth>
                    <WithdrawalReviewRules />
                  </RequireAuth>
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.WITHDRAWAL_REVIEW_ADD_RULE}
                element={
                  <RequireAuth>
                    <AddRulePage />
                  </RequireAuth>
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.WITHDRAWAL_REVIEW_EDIT_RULE}
                element={
                  <RequireAuth>
                    <EditRulePage />
                  </RequireAuth>
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.EVENT_DETAILS}
                element={
                  <RequireAuth>
                    <EventDetails />
                  </RequireAuth>
                }
              />
              <Route
                path={NAVIGATION_ROUTES?.BETS_TYPES_LIST}
                element={
                  <RequireAuth>
                    <BetsTypesList />
                  </RequireAuth>
                }
              />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      )}
    </div>
  );
};

export default App;
